
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { overProvisionData } from "service/service";
import LoaderComponent from "components/Loader";

interface OverProvisionedPieChartProps {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
}

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
  maximumFractionDigits: 2,
});

const OverProvisionedPieChart: React.FC<OverProvisionedPieChartProps> = ({
  startDate,
  endDate,
  env,
  sector,
  location,
  selectedDateInString,
}) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [overProvisionedData, setOverProvisionedData] = useState<
    { _id: string; total_count: number }[]
  >([]);

  const chartRef = useRef<any>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(
    theme.breakpoints.between("sm", "md")
  );

  // Resize chart when its container changes size
  useEffect(() => {
    const chartDom = chartRef.current
      ?.getEchartsInstance()
      ?.getDom();

    if (!chartDom) return;

    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance()?.resize();
    });

    resizeObserver.observe(chartDom);

    return () => resizeObserver.disconnect();
  }, []);

  // Fetch over-provisioned data
  const overProvisionedDataFetch = useCallback(async () => {
    try {
      setLoading(true);

      const startFormatted = startDate.split(" ")[0];
      const endFormatted = endDate.split(" ")[0];

      const resp = await overProvisionData(
        startFormatted,
        endFormatted,
        env,
        sector,
        location,
        selectedDateInString
      );

      if (resp?.success) {
        setOverProvisionedData(resp.data || []);
      } else {
        setOverProvisionedData([]);
      }
    } catch (err) {
      console.error(
        "Failed to fetch over provisioned clusters:",
        err
      );
      setOverProvisionedData([]);
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
  ]);

  useEffect(() => {
    overProvisionedDataFetch();
  }, [overProvisionedDataFetch]);

  // Prepare pie chart data
  const overProvisionedPieData = overProvisionedData.map((item) => ({
    name: item._id,
    value: item.total_count,
  }));

  const total = overProvisionedPieData.reduce(
    (sum, item) => sum + item.value,
    0
  );

  const formattedTotal = compactFormatter.format(total);

  // Navigate when Total is clicked
  const handleTotalClick = () => {
    navigate("/databricksADB/over-provisioned", {
      state: {
        startDate,
        endDate,
        env,
        sector,
        location,
        selectedDateInString,
      },
    });
  };

  // Navigate when a pie slice is clicked
  const handlePieClickEvents = (params: any) => {
    navigate(
      `/databricksADB/over-provisioned?type=${encodeURIComponent(
        params.name
      )}`,
      {
        state: {
          startDate,
          endDate,
          env,
          sector,
          location,
          selectedDateInString,
        },
      }
    );
  };

  // Pie chart configuration
  const overprovisionedChart: EChartsOption = {
    tooltip: {
      backgroundColor: "#ffffff",
      trigger: "item",
      textStyle: {
        color: "#000000",
      },
    },

    // Keep the legend visible at the bottom
    legend: {
      orient: "horizontal",
      bottom: 0,
      left: "6%",
      width: "90%",
      itemGap: 12,
      textStyle: {
        color: "#000000",
        fontSize: isSmallScreen ? 10 : 12,
      },
    },

    grid: {
      top: 0,
      bottom: 45,
      containLabel: true,
    },

    color: ["#00BF7D", "#299BFF"],

    series: [
      {
        name: "Over Provisioned Clusters",
        type: "pie",

        radius: (() => {
          if (isSmallScreen) return "48%";
          if (isMediumScreen) return "60%";
          return "65%";
        })(),

        // Move the pie slightly upward to leave room for the legend
        center: ["50%", "38%"],

        data: overProvisionedPieData,
        avoidLabelOverlap: false,

        label: {
          show: !isSmallScreen,
          position: "inside",
          color: "#fff",
          fontSize: isSmallScreen ? 11 : 15,

          formatter: (params: any) => {
            const value = Number(params.value ?? 0);
            return compactFormatter.format(value);
          },
        },

        emphasis: {
          scale: true,
          scaleSize: 8,

          itemStyle: {
            shadowBlur: 12,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.4)",
          },
        },
      },
    ],
  };

  // Render loading, empty state, or chart
  const renderChartContent = () => {
    if (loading) {
      return <LoaderComponent size={50} />;
    }

    if (overProvisionedData.length === 0) {
      return (
        <Box
          sx={{
            color: "#fff",
            textAlign: "center",
            py: 4,
            width: "100%",
          }}
        >
          No data available
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          mt: 1,
        }}
      >
        {/* Title and Total stacked on the left */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            width: "100%",
            gap: 0.5,
            px: 1,
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="widgetTitle"
            sx={{
              textAlign: "left",
              width: "100%",
              color: "#000000",
            }}
          >
            Over Provisioned Clusters
          </Typography>

          <Typography
            variant={isSmallScreen ? "h4" : "h3"}
            onClick={handleTotalClick}
            sx={{
              color: "#000000",
              fontWeight: "400",
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.3s ease",

              "&:hover": {
                color: "#21ddebff",
                transform: "scale(1.08)",
                transformOrigin: "left center",
              },
            }}
          >
            {formattedTotal}
          </Typography>
        </Box>

        {/* Centered pie chart with visible legend */}
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ReactECharts
            ref={chartRef}
            option={overprovisionedChart}
            onEvents={{
              click: handlePieClickEvents,
            }}
            style={{
              height: isSmallScreen ? 240 : 290,
              width: "100%",
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        color: "#fff",
        height: isSmallScreen ? "auto" : 380,
        maxHeight: isSmallScreen ? "none" : 380,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {renderChartContent()}
    </Box>
  );
};

export default OverProvisionedPieChart;
